export interface ColorVars {
  color: string; // background rgba
  textColor: string; // foreground rgba (auto: light on dark bg, dark on light bg)
  unitBg: string; // tinted background for unit boxes
}

/// User-facing color presets. Matches
/// `ColorPresetsManager.defaultBackgroundColorPresets` in the iOS app so the
/// same six default swatches appear in both clients.
export const DEFAULT_PRESETS: number[] = [
  0xc12d48,
  0xf2a33c,
  0xd1b552,
  0x458846,
  0x306fa0,
  0x7952a3,
];

/// Auto-color for the given date, matching
/// `ColorPresetsManager.colorForDayOfYear(for:)` in the iOS app. Same hue
/// cycle (4×/year), same orange-band boost, same saturation/brightness wobble
/// — so a countdown created on April 21 in either client lands on the same
/// spring green.
export function colorForDate(date: Date): number {
  return colorForDayOfYear(dayOfYear(date));
}

function dayOfYear(date: Date): number {
  const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  let day = date.getDate();
  for (let m = 0; m < date.getMonth(); m++) {
    day += daysInMonth[m];
    if (m === 1 && isLeapYear(date.getFullYear())) day += 1;
  }
  return day;
}

function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

function colorForDayOfYear(day: number): number {
  // Mirrors ColorPresetsManager.color(dayOfYear:) in Swift line-for-line.
  const d = Math.max(0, Math.min(364, day - 1));
  const cyclesPerYear = 4;
  const hueStep = cyclesPerYear / 365.0;
  const hueSeed = 0.0686;
  const baseSat = 0.6;
  const baseBright = 0.6;

  let hue = (hueSeed + d * hueStep) % 1;
  if (hue < 0) hue += 1;

  const orangeBoost = Math.exp(-Math.pow((hue - 0.1) / 0.07, 2));
  const saturation = Math.min(
    0.92,
    Math.max(0.4, baseSat + 0.06 * Math.cos((d * Math.PI * 2) / 13.0 + 1.1) + 0.25 * orangeBoost),
  );
  const brightness = Math.min(
    0.92,
    Math.max(0.32, baseBright + 0.05 * Math.sin((d * Math.PI * 2) / 17.0) + 0.28 * orangeBoost),
  );

  return hsbToHex(hue, saturation, brightness);
}

function hsbToHex(h: number, s: number, b: number): number {
  const c = b * s;
  const hp = (((h % 1) + 1) % 1) * 6.0;
  const x = c * (1.0 - Math.abs((hp % 2) - 1.0));
  let r1 = 0;
  let g1 = 0;
  let b1 = 0;
  if (hp < 1) { r1 = c; g1 = x; }
  else if (hp < 2) { r1 = x; g1 = c; }
  else if (hp < 3) { g1 = c; b1 = x; }
  else if (hp < 4) { g1 = x; b1 = c; }
  else if (hp < 5) { r1 = x; b1 = c; }
  else { r1 = c; b1 = x; }
  const m = b - c;
  const r = Math.round((r1 + m) * 255);
  const g = Math.round((g1 + m) * 255);
  const bb = Math.round((b1 + m) * 255);
  return ((r << 16) | (g << 8) | bb) >>> 0;
}

export function hexStringToInt(hex: string): number {
  return parseInt(hex.replace('#', ''), 16) >>> 0;
}

export function intToHexString(n: number): string {
  return '#' + (n >>> 0).toString(16).padStart(6, '0');
}

/// Mirrors functions/index.js:colorVars so PWA-rendered countdowns adopt the
/// same auto-contrast scheme as the server-rendered /to page.
export function colorVars(num: number): ColorVars {
  const u = num >>> 0;
  const b = u & 0xff;
  const g = (u & 0xff00) >>> 8;
  const r = (u & 0xff0000) >>> 16;
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  const dark = luminance < 140;
  return {
    color: `rgba(${r},${g},${b},1)`,
    textColor: dark ? 'rgba(255,255,255,0.95)' : 'rgba(0,0,0,0.85)',
    unitBg: dark ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.08)',
  };
}
